import ReactApexChart from 'react-apexcharts';

import { ApexOptions } from 'apexcharts';
import { format } from 'date-fns';
import styled, { useTheme } from 'styled-components';

import round from 'utils/round';

interface IBasicColumnChart {
	title?: string;
	data_name: string;
	datas: number[];
	categorys: (string | string[])[];
	width?: string;
	height?: string;
	radius?: number;
	trend?: any;
	_xaxis?: boolean;
	_yaxis?: boolean;
	bar_width?: string;
	dataLabels?: boolean;
	dataLabelsGap?: number;
	zoom?: boolean;
	xTickAmount?: number;
	toolbar_offsetY?: number;
}

const BasicColumnChart = ({
	title,
	data_name,
	datas,
	categorys,
	width,
	height,
	radius = 4,
	trend,
	_xaxis,
	_yaxis,
	bar_width,
	dataLabels = true,
	dataLabelsGap = 10,
	zoom = true,
	xTickAmount = 8,
	toolbar_offsetY = 0,
}: IBasicColumnChart) => {
	const theme = useTheme();

	const diff = Math.max(...datas) - Math.min(...datas) ?? 0;
	const min = Math.min(...datas) - diff <= 0 ? 0 : Math.min(...datas) - diff;

	const options: ApexOptions = {
		title: {
			text: title ?? '',
			align: 'left',
			style: {
				fontSize: '24px',
				fontFamily: 'SUIT',
				fontWeight: 'bold',
				color: theme.colors.uplus.primary50_key,
			},
		},
		chart: {
			stacked: false,
			toolbar: {
				show: true,
				tools: {
					zoom: false,
					zoomin: true,
					zoomout: true,
					download: false,
				},
				offsetY: toolbar_offsetY,
				offsetX: -8,
			},
			zoom: {
				enabled: zoom,
				autoScaleYaxis: true,
			},
		},
		plotOptions: {
			bar: {
				borderRadius: radius,
				dataLabels: {
					position: 'top', // top, center, bottom
					maxItems: 8,
					hideOverflowingLabels: true, // only work on stacked-charts
				},
				columnWidth: bar_width ?? '70%',
			},
		},
		grid: {
			padding: {
				left: 40,
				right: 40,
			},
		},
		dataLabels: {
			enabled: dataLabels,
			offsetY: -20,
			style: {
				fontSize: '12px',
				colors: ['#304758'],
			},
			formatter: function (val: number, { seriesIndex, dataPointIndex, w }) {
				const gap = Math.floor(datas?.length / dataLabelsGap);

				if (gap < 1) {
					return round(val, 'round') ?? 0;
				}

				if (dataPointIndex % gap !== 0) {
					return '';
				}

				return round(val, 'round') ?? 0;
			},
		},
		colors: [
			trend > 0
				? theme.colors.uplus.primary50_key
				: trend < 0
				? theme.colors.primary.primary50_key
				: theme.colors.gray.gray15,
		],
		xaxis: {
			type: 'category',
			labels: {
				show: _xaxis ?? true,
				// formatter: function (val: string) {
				// 	return format(new Date(val), 'MM.dd');
				// },
				hideOverlappingLabels: true,
				rotate: 0,
			},

			categories: categorys ?? [],
			tickAmount: xTickAmount,
			tickPlacement: zoom ? 'on' : 'between',
			axisBorder: {
				show: false,
			},
			axisTicks: {
				show: false,
			},
			tooltip: {
				enabled: false,
			},
		},
		yaxis: [
			{
				opposite: true,
				labels: {
					show: _yaxis ?? true,
					formatter: function (val: number) {
						return val?.toLocaleString();
					},
				},
				tickAmount: 4,
				min: datas?.length > 1 && new Set(datas)?.size !== 1 ? min : undefined,
			},
		],
		tooltip: {
			intersect: false,
		},
	};

	return (
		<ReactApexChart
			options={options}
			series={[
				{
					name: data_name ?? '',
					data: datas ?? [],
				},
			]}
			type="bar"
			width={width ?? '100%'}
			height={height ?? '100%'}
		/>
	);
};

const ChartContainer = styled.div`
	.apexcharts-toolbar {
		z-index: 0;
	}
`;

export default BasicColumnChart;
